import type React from 'react';

import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { User01 } from '@untitledui/icons/User01';
import { useEffect, useRef, useState } from 'react';

import { Avatar } from '@/components/base/avatar/avatar';
import { Button } from '@/components/base/buttons/button';
import { notify } from '@/components/base/notifications/notifications';
import { useUploadProfilePicture } from '@/hooks/api/use-upload-profile-picture';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE_MB = 5;

interface ProfilePictureUploadProps {
  userId: string;
  onUploadSuccess: () => void;
  onCancel: () => void;
  /** Hide the built-in heading when the surrounding page provides its own. */
  hideHeading?: boolean;
  /** Label for the dismiss button; defaults to "Cancel". */
  cancelLabel?: React.ReactNode;
}

export function ProfilePictureUpload({
  userId,
  onUploadSuccess,
  onCancel,
  hideHeading = false,
  cancelLabel,
}: ProfilePictureUploadProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadProfilePicture = useUploadProfilePicture();

  // Avoids memory leaks by revoking the object URL when the component unmounts
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      notify({
        title: t`Invalid file type`,
        description: t`Please upload a JPEG, PNG, or WebP image`,
        color: 'error',
      });
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      notify({
        title: t`File too large`,
        description: t`Please upload an image smaller than ${MAX_FILE_SIZE_MB}MB`,
        color: 'error',
      });
      return;
    }

    setSelectedFile(file);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      notify({
        title: t`No image selected`,
        description: t`Please select a profile picture to upload`,
        color: 'error-primary',
      });
      return;
    }

    try {
      await uploadProfilePicture.mutateAsync({
        file: selectedFile,
        userId,
      });
      onUploadSuccess();
    } catch (error) {
      notify({
        title: t`Failed to upload profile picture`,
        description:
          error instanceof Error ? error.message : t`An unknown error occurred`,
        color: 'error',
      });
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {hideHeading ? null : (
        <>
          <h2 slot="title" className="mt-4 text-lg font-semibold text-primary">
            <Trans>Add a profile picture</Trans>
          </h2>

          <p className="mt-1 text-sm text-tertiary">
            <Trans>
              Adding a profile picture helps researchers identify you and
              increases your chances of being selected for projects.
            </Trans>
          </p>
        </>
      )}

      <div className="mt-5 flex flex-col items-center gap-4">
        <div className="group relative flex flex-col items-center gap-2">
          {previewUrl ? (
            <Avatar src={previewUrl} size="2xl" alt="Profile picture preview" />
          ) : (
            <div className="flex size-16 items-center justify-center rounded-full border-2 border-dashed border-secondary bg-secondary transition group-hover:border-brand-solid group-hover:bg-primary_hover">
              <User01 className="size-8 text-fg-quaternary" />
            </div>
          )}
          <Button
            className="mt-2 text-center text-sm font-medium"
            onClick={() => fileInputRef.current?.click()}
            isDisabled={uploadProfilePicture.isPending}
          >
            {previewUrl ? (
              <Trans>Change photo</Trans>
            ) : (
              <Trans>Choose photo</Trans>
            )}
          </Button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES.join(',')}
          onChange={handleFileSelect}
          className="hidden"
          data-testid="participant-profile-avatar-input"
        />

        <p className="text-xs text-tertiary">
          <Trans>JPEG, PNG, or WebP. Max {MAX_FILE_SIZE_MB}MB.</Trans>
        </p>
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <Button color="secondary" onClick={onCancel}>
          {cancelLabel ?? <Trans>Cancel</Trans>}
        </Button>
        <Button
          color="primary"
          onClick={handleUpload}
          isDisabled={!selectedFile}
          isLoading={uploadProfilePicture.isPending}
          data-testid="participant-profile-upload-button"
        >
          <Trans>Upload</Trans>
        </Button>
      </div>
    </div>
  );
}
